import { Button, cn, Input, Label } from '@up2stay/up2promo-ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon } from '@iconify/react';
import ButtonWithEnter from '../components/button-with-enter';
import { SetupStepShell } from '../components/setup-step-shell';
import { useIntercom } from '@up2promo-setup-frontend/core/hooks/use-intercom';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';
import { fadeInUp } from '@up2promo-setup-frontend/ui/animations/fade-in-up.animation';
import MessageModal from '@up2promo-setup-frontend/ui/components/modals/message.modal';
import { useDictionary } from '@up2promo-setup-frontend/ui/providers/dictionary.provider';
import { motion } from 'motion/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { useDialogManager } from '@up2promo-setup-frontend/core/hooks/use-dialog-manager';
import { setSuggestionsForGoogle } from './utils/suggestions.utils';
import { useSetupNavigation } from './hooks/use-setup-navigation';
import { Navigate, useParams } from 'react-router-dom';
import { ProductType } from '@up2promo-setup-frontend/core/types/product-type.enum';

export default function AddressPage() {
  const translations = useDictionary();
  const { goNext } = useSetupNavigation();
  const { hash } = useParams<{ hash: string }>();

  // Use selectors to only subscribe to needed values - ALL hooks must be called before any early returns
  const type = useSetupStore((state) => state.type);
  const address = useSetupStore((state) => state.address);
  const input = useSetupStore((state) => state.input);
  const setAddress = useSetupStore((state) => state.setAddress);

  const intercom = useIntercom({
    user: hash ? { hash } : undefined,
    enabled: true,
  });

  const addressSchema = z.object({
    address: z.string().optional(),
  });
  type AddressInfoValues = z.infer<typeof addressSchema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddressInfoValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    resolver: zodResolver(addressSchema as never),
    defaultValues: {
      address: address || '',
    },
  });

  const { openDialog } = useDialogManager();
  const [isLoading, setLoading] = useState(false);

  const onSubmit = async (data: AddressInfoValues) => {
    try {
      setLoading(true);
      const trimmedAddress = data.address?.trim() || undefined;
      setAddress(trimmedAddress);

      // Fetch suggestions with business name and optional address
      if (input) {
        await setSuggestionsForGoogle(input, trimmedAddress);
      }

      // Track address provided
      if (intercom.isBooted()) {
        const suggestions = useSetupStore.getState().suggestions;
        intercom.trackEvent('address-submitted', {
          hash,
          address_provided: !!trimmedAddress,
          address_length: trimmedAddress?.length || 0,
          current_type: type,
          business_name: input,
          suggestions_count: suggestions?.length || 0,
        });
      }

      goNext('address');
    } catch {
      // Track error
      if (intercom.isBooted()) {
        intercom.trackEvent('address-error', {
          hash,
          error_type: 'suggestion_fetch_failed',
          current_type: type,
          business_name: input,
        });
      }

      void openDialog(MessageModal, {
        componentProps: {
          title: translations.pages.inputStep.error.title,
          bodyText: translations.pages.inputStep.error.description,
          isDestructive: true,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = useCallback(async () => {
    try {
      setLoading(true);
      setAddress(undefined);

      // Fetch suggestions with just business name (no address)
      if (input) {
        await setSuggestionsForGoogle(input, undefined);
      }

      // Track address skipped
      if (intercom.isBooted()) {
        const suggestions = useSetupStore.getState().suggestions;
        intercom.trackEvent('address-skipped', {
          hash,
          address_provided: false,
          current_type: type,
          business_name: input,
          suggestions_count: suggestions?.length || 0,
        });
      }

      goNext('address');
    } catch {
      // Track error
      if (intercom.isBooted()) {
        intercom.trackEvent('address-error', {
          hash,
          error_type: 'suggestion_fetch_failed',
          current_type: type,
          business_name: input,
        });
      }

      void openDialog(MessageModal, {
        componentProps: {
          title: translations.pages.inputStep.error.title,
          bodyText: translations.pages.inputStep.error.description,
          isDestructive: true,
        },
      });
    } finally {
      setLoading(false);
    }
  }, [
    setAddress,
    goNext,
    intercom,
    hash,
    type,
    input,
    openDialog,
    translations,
  ]);

  // Guard: Redirect if not Google type (address step only for Google)
  // MUST be after all hooks to maintain hook order
  if (type !== ProductType.GOOGLE) {
    const basePath = hash ? `/${hash}` : '';
    return <Navigate to={`${basePath}/select-suggestion`} replace />;
  }

  return (
    <SetupStepShell>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-2 sm:gap-3"
      >
        <motion.h1
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="text-2xl font-semibold tracking-tight text-foreground text-balance md:text-3xl lg:text-4xl"
        >
          {translations.pages.addressStep.title}
        </motion.h1>
        <motion.p
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="max-w-prose text-sm text-foreground/70 text-pretty md:text-base"
        >
          {translations.pages.addressStep.description}
        </motion.p>

        <motion.div
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="my-4 flex flex-col gap-2 sm:my-6"
        >
          <Label htmlFor="setup-address">
            {translations.pages.addressStep.inputTitle}
          </Label>
          <div className="flex flex-col gap-1">
            <Input
              id="setup-address"
              autoFocus
              {...register('address')}
              placeholder={translations.pages.addressStep.inputPlaceholder}
              aria-invalid={!!errors.address}
              className={cn(errors.address && 'border-destructive')}
              // Defensive attributes to prevent browser/extension DOM mutations
              // that cause React removeChild crashes on route transition
              autoComplete="street-address"
              autoCorrect="off"
              autoCapitalize="words"
              spellCheck={false}
              data-lpignore="true"
              data-form-type="other"
            />
            {errors.address?.message && (
              <p className="text-sm text-destructive">
                {errors.address.message}
              </p>
            )}
          </div>
        </motion.div>

        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
        >
          <ButtonWithEnter
            type="submit"
            text={translations.pages.addressStep.button}
            isLoading={isLoading}
          />
        </motion.div>

        <motion.div
          custom={4}
          initial="hidden"
          animate="visible"
          variants={fadeInUp}
          className="mt-4 flex flex-col gap-2 sm:mt-6 sm:gap-3"
        >
          <div className="flex items-start gap-2 rounded-lg border border-border bg-card p-3 sm:gap-3 sm:p-4">
            <Icon
              icon="mdi:information-outline"
              className="mt-0.5 shrink-0 text-foreground"
              width={18}
            />
            <p className="text-xs text-foreground/70 sm:text-sm">
              {translations.pages.addressStep.skipDescription}
            </p>
          </div>
          <Button type="button" variant="ghost" onClick={handleSkip}>
            {translations.pages.addressStep.skipButton}
          </Button>
        </motion.div>
      </form>
    </SetupStepShell>
  );
}
